import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { getTextFields } from "./qrLabelUtils";

// Everything here uses inline styles in millimetres so the on-screen preview and the printed page match exactly.

const FONT = "Arial, Helvetica, sans-serif";

const TextLine = ({ field, cell, fontPt, align }) => (
  <div
    style={{
      fontSize: `${fontPt}pt`,
      lineHeight: `${cell.lh}mm`,
      fontWeight: field.bold ? 700 : 400,
      textAlign: align,
      width: "100%",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: field.max,
      wordBreak: "break-word",
    }}
  >
    {field.text}
  </div>
);

export const LabelCell = ({ item, cell, s }) => {
  const fields = getTextFields(item, s);
  const side = s.template === "qr_side";

  const qr = (
    <QRCodeSVG
      value={item.url}
      size={256}
      level={s.errorLevel}
      marginSize={1}
      style={{
        width: `${cell.qr}mm`,
        height: `${cell.qr}mm`,
        flex: "none",
        display: "block",
      }}
    />
  );

  const textBlock = (list, align) =>
    list.length ? (
      <div
        style={{
          width: side ? `${cell.textW}mm` : `${cell.qr}mm`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {list.map((f) => (
          <TextLine
            key={f.key}
            field={f}
            cell={cell}
            fontPt={s.fontPt}
            align={align}
          />
        ))}
      </div>
    ) : null;

  let content;
  if (side) {
    content = (
      <>
        {qr}
        <div
          style={{
            width: `${cell.textW}mm`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {fields.map((f) => (
            <TextLine
              key={f.key}
              field={f}
              cell={cell}
              fontPt={s.fontPt}
              align="left"
            />
          ))}
        </div>
      </>
    );
  } else if (s.template === "name_top") {
    content = (
      <>
        {textBlock(
          fields.filter((f) => f.key === "product"),
          "center",
        )}
        {qr}
        {textBlock(
          fields.filter((f) => f.key !== "product"),
          "center",
        )}
      </>
    );
  } else {
    content = (
      <>
        {qr}
        {textBlock(fields, "center")}
      </>
    );
  }

  return (
    <div
      style={{
        width: `${cell.w}mm`,
        height: `${cell.h}mm`,
        boxSizing: "border-box",
        padding: `${cell.pad}mm`,
        border: s.showBorder
          ? "0.2mm dashed #9ca3af"
          : "0.2mm solid transparent",
        background: "#fff",
        color: "#000",
        fontFamily: FONT,
        display: "flex",
        flexDirection: side ? "row" : "column",
        alignItems: "center",
        justifyContent: "center",
        gap: `${cell.inner}mm`,
        overflow: "hidden",
      }}
    >
      {content}
    </div>
  );
};

export const LabelPage = ({ labels, layout, s, isLast }) => {
  const { pw, ph, cell, cols, rows } = layout;
  return (
    <div
      style={{
        width: `${pw}mm`,
        height: `${ph - 1}mm`, // 1mm shorter than the sheet so browsers never add a blank page
        boxSizing: "border-box",
        padding: `${s.marginMm}mm`,
        background: "#fff",
        overflow: "hidden",
        pageBreakAfter: isLast ? "auto" : "always",
        breakAfter: isLast ? "auto" : "page",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${cell.w}mm)`,
          gridTemplateRows: `repeat(${rows}, ${cell.h}mm)`,
          gap: `${s.gapMm}mm`,
          justifyContent: s.centerGrid ? "center" : "start",
          alignContent: s.centerGrid ? "center" : "start",
          width: "100%",
          height: "100%",
        }}
      >
        {labels.map((item, i) =>
          item ? (
            <LabelCell key={i} item={item} cell={cell} s={s} />
          ) : (
            <div key={i} />
          ),
        )}
      </div>
    </div>
  );
};

export const LabelPages = ({ pages, layout, s }) => (
  <>
    {pages.map((labels, i) => (
      <LabelPage
        key={i}
        labels={labels}
        layout={layout}
        s={s}
        isLast={i === pages.length - 1}
      />
    ))}
  </>
);
